import React from 'react';
import { RouterProvider, createBrowserRouter } from 'react-router-dom';
import 'react-material-symbols/rounded';

// Contexts
import { AuthProvider } from './contexts/AuthContext.js';
import { ParentProvider } from './contexts/ParentContext.js';
import { StaffNotificationsProvider } from './contexts/StaffNotificationsContext.jsx';
// import { NotificationsProvider } from './contexts/NotificationsContext';

// Routes
import ProtectedParentRoute from './components/ProtectedParentRoute.jsx';
import ProtectedRoute from './components/ProtectedRoute.jsx';
import PublicParentRoute from './components/PublicParentRoute.jsx';
import PublicRoute from './components/PublicRoute.jsx';

// Components
import AddEmployeeForm from './components/employee/AddEmployeeForm';
import AttendanceCalendar from './components/parentDashboard/AttendanceCalendar.jsx';
import Layout from './components/dashboard/layout/layout.js';
import { ParentDashboard } from './components/parent/ParentDashboard';
import ParentDashboardLayout from './components/parentDashboard/ParentDashboardLayout.jsx';
import { ParentTable } from './components/parent/ParentTable';
import StudentDashboard from './components/students/StudentDashboard.js';

// Pages - Main Dashboard
import AcademicDashboard from './pages/AcademicDashboard.jsx';
import Accounts from './pages/Accounts.jsx';
import AddStudent from './pages/AddStudent.jsx';
import Analytics from './pages/Analytics.jsx';
import Attendance from './pages/ParentDashboard/Dashboard/Attendance.jsx';
import Dashboard from './pages/Dashboard.jsx';
import { EmployeeProfile } from './pages/EmployeeProfile.jsx';
import Events from './pages/Events.jsx';
import Login from './pages/Login.jsx';
import MainDashboardForgotPassword from './pages/MainDashboardForgotPassword.jsx';
import MainDashboardPasswordReset from './pages/MainDashboardPasswordReset.jsx';
import NewParent from './pages/NewParent.jsx';
import OTP from './pages/OTP.jsx';
import ParentOTP from './pages/ParentOTP.jsx';
import SchoolSignUp from './pages/SchoolSignUp.jsx';
import StaffAttendance from './pages/Attendance.jsx';
import Students from './pages/Students.jsx';
import Subject from './pages/Subject.jsx';
import Support from './pages/Support.jsx';
import { TeamMembers } from './pages/TeamMembers.jsx';

// Pages - Parent Dashboard
import DashboardMain from './pages/ParentDashboard/Dashboard/DashboardMain.jsx';
import Event from './pages/ParentDashboard/Dashboard/Event.jsx';
import ParentDashboardForgotPassword from './pages/ParentDashboard/Auth/ParentDashboardForgotPassword.jsx';
import ParentDashboardPasswordReset from './pages/ParentDashboard/Auth/ParentDashboardPasswordReset.jsx';
import ParentDashboardSignIn from './pages/ParentDashboard/Auth/ParentDashboardSignIn.jsx';
import ParentGuide from './pages/ParentDashboard/Dashboard/ParentGuide.jsx';
import Payment from './pages/ParentDashboard/Dashboard/Payment.jsx';
import SelectChild from './pages/ParentDashboard/Dashboard/SelectChild.jsx';
import StudentDetails from './pages/StudentDetails.jsx';
import StudentProfile from './pages/ParentDashboard/Dashboard/StudentProfile.jsx';
import Teachers from './pages/ParentDashboard/Dashboard/Teachers.jsx';

// Pages - Classes and Sessions
import ClassList from './pages/ClassList.jsx';
import ClassPage from './pages/Class.jsx';
import SessionDetails from './pages/SessionDetails.jsx';
import SessionsPage from './pages/Sessions.jsx';

// Pages - Departments
import DepartmentDashboard from './pages/Department.jsx';
import DepartmentDetails from './pages/DepartmentDetails.jsx';

// Pages - Settings (Parent Dashboard)
import AccountInformation from './pages/ParentDashboard/Dashboard/Settings/AccountInformation.jsx';
import Notifications from './pages/ParentDashboard/Dashboard/Settings/Notifications.jsx';
import Security from './pages/ParentDashboard/Dashboard/Settings/Security.jsx';
import Settings from './pages/ParentDashboard/Dashboard/Settings/index.jsx';

// Pages - App Settings (Profile)
import ProfileSettingsIndex from './pages/AppSettings/ProfileSettings/Index.jsx';
import StaffAccountInformation from './pages/AppSettings/ProfileSettings/AccountInformation.jsx';
import StaffProfileNotifications from './pages/AppSettings/ProfileSettings/Notifications.jsx';
import StaffProfileSecurity from './pages/AppSettings/ProfileSettings/Security.jsx';

// Pages - App Settings (Org)
import General from './pages/AppSettings/OrgSettings/General.jsx';
import OrgSettings from './pages/AppSettings/OrgSettings/Index.jsx';
import RecordsManagement from './pages/AppSettings/OrgSettings/RecordsManagement.jsx';
import RolesManagement from './pages/AppSettings/OrgSettings/RolesManagement.jsx';
import SecurityManagement from './pages/AppSettings/OrgSettings/Security.jsx';
import SubscriptionManagement from './pages/AppSettings/OrgSettings/SubscriptionManagement.jsx';
import UsersManagement from './pages/AppSettings/OrgSettings/UsersManagement.jsx';

const router = createBrowserRouter([
  {
    path: '/',
    element: (
      <AuthProvider>
        <ProtectedRoute>
          <StaffNotificationsProvider>
            <Layout />
          </StaffNotificationsProvider>
        </ProtectedRoute>
      </AuthProvider>
    ),
    children: [
      {
        index: true,
        element: <Dashboard />,
      },
      {
        path: 'student',
        element: <Students />,
        handle: {
          title: 'Students',
          subtitle: 'Manage Students and their profiles',
        },
      },
      {
        path: 'student/list',
        element: <StudentDashboard />,
      },
      {
        path: 'student/details/:studentId',
        element: <StudentDetails />,
      },
      {
        path: 'student/add',
        element: <AddStudent />,
      },
      {
        path: 'parent/new',
        element: <NewParent />,
      },
      {
        path: 'team',
        element: <TeamMembers />,
        handle: {
          title: 'Team',
          subtitle: 'Manage Team Members and Staff',
        },
      },
      {
        path: 'team/profile',
        element: <EmployeeProfile />,
      },
      {
        path: 'team/profile/:staffId',
        element: <EmployeeProfile />,
      },
      {
        path: 'team/add',
        element: <AddEmployeeForm />,
      },
      {
        path: 'events',
        element: <Events />,
        handle: {
          title: 'Events',
          subtitle: 'Manage Events and Calendar',
        },
      },
      {
        path: 'events/calendar',
        element: <Events view="calendar" />,
      },
      {
        path: 'events/:eventId',
        element: <Events view="details" />,
      },
      {
        path: 'analytics',
        element: <Analytics />,
        handle: {
          title: 'Analytics',
          subtitle: 'Manage Analytics ',
        },
      },
      {
        path: 'settings',
        handle: {
          title: 'Profile Settings',
          subtitle: 'Manage Your Profile Settings',
        },
        element: <ProfileSettingsIndex />,
        children: [
          { index: true, element: <StaffAccountInformation /> },
          { path: 'security', element: <StaffProfileSecurity /> },
          { path: 'notifications', element: <StaffProfileNotifications /> },
        ],
      },
      {
        path: 'org/settings',
        handle: {
          title: 'School Settings',
          subtitle: 'Setup and edit system settings and preferences',
        },
        element: <OrgSettings />,
        children: [
          { index: true, element: <General /> },
          { path: 'records', element: <RecordsManagement /> },
          { path: 'users', element: <UsersManagement /> },
          { path: 'roles', element: <RolesManagement /> },
          { path: 'subscription', element: <SubscriptionManagement /> },
          { path: 'security', element: <SecurityManagement /> },
        ],
      },
      {
        path: 'accounts',
        element: <Accounts />,
        handle: {
          title: 'Accounts',
          subtitle: 'Manage Account Metrics',
        },
      },
      {
        path: 'attendance',
        element: <StaffAttendance />,
        handle: {
          title: 'Attendance',
          subtitle: 'Manage Attendance Metrics',
        },
      },
      // Sessions route with title and subtitle for header
      {
        path: 'sessions',
        element: <SessionsPage />,
        handle: {
          title: 'Sessions',
          subtitle: 'Manage academic sessions and terms',
        },
      },
      {
        path: 'sessions/:sessionId',
        element: <SessionDetails />,
        handle: {
          title: 'Session Details',
          subtitle: 'View and manage session information',
        },
      },
      // Parent routes
      {
        path: 'parents',
        element: <ParentTable />,
        handle: {
          title: 'Parents',
          subtitle: 'Manage Parents and Guardians',
        },
      },
      {
        path: 'parents/dashboard',
        element: <ParentDashboard />,
      },

      {
        path: 'parent/new',
        element: <NewParent />, // Keep existing parent/new route for backward compatibility
      },
      {
        path: 'departments',
        element: <DepartmentDashboard />,
        handle: {
          title: 'Department Management',
          subtitle: 'Manage and organize departments',
        },
      },
      {
        path: 'departments/:deptId',
        element: <DepartmentDetails />,
        handle: {
          title: 'Department Details',
          subtitle: 'View and manage department information',
        },
      },
      {
        path: 'academics',
        element: <AcademicDashboard />,
        handle: {
          title: 'Academic Dashboard',
          subtitle: 'Manage academic activities and performance',
        },
      },
      {
        path: 'classes',
        element: <ClassList />,
        handle: {
          title: 'Class Management',
          subtitle: 'Manage class schedules and performance',
        },
      },
      {
        path: 'classes/:classId',
        element: <ClassPage />,
        handle: {
          title: 'Class Management',
          subtitle: 'Manage class schedules and performance',
        },
      },
      {
        path: 'subjects',
        element: <Subject />,
        handle: {
          title: 'Subject Management',
          subtitle: 'Manage subjects, curriculum, and performance',
        },
      },
      {
        path: 'support',
        element: <Support />,
        handle: {
          title: 'Support',
          subtitle: 'Get help and chat with our team',
        },
      },
    ],
  },
  {
    path: '/otp',
    element: (
      <AuthProvider>
        <ProtectedRoute>
          <OTP />
        </ProtectedRoute>
      </AuthProvider>
    ),
  },
  {
    path: '/parent/otp',
    element: (
      <ParentProvider>
        <ProtectedParentRoute>
          <ParentOTP />
        </ProtectedParentRoute>
      </ParentProvider>
    ),
  },
  {
    path: '/login',
    element: (
      <AuthProvider>
        <PublicRoute>
          <Login />
        </PublicRoute>
      </AuthProvider>
    ),
  },
  {
    path: '/signup',
    element: (
      <AuthProvider>
        <PublicRoute>
          <SchoolSignUp />
        </PublicRoute>
      </AuthProvider>
    ),
  },
  {
    path: '/parent/login',
    element: (
      <ParentProvider>
        <PublicParentRoute>
          <ParentDashboardSignIn />
        </PublicParentRoute>
      </ParentProvider>
    ),
  },
  {
    path: '/parent/forgot-password',
    element: (
      <ParentProvider>
        <PublicParentRoute>
          <ParentDashboardForgotPassword />
        </PublicParentRoute>
      </ParentProvider>
    ),
  },
  {
    path: '/forgot-password',
    element: (
      <AuthProvider>
        <PublicRoute>
          <MainDashboardForgotPassword />
        </PublicRoute>
      </AuthProvider>
    ),
  },
  {
    path: '/parent/password-reset/:token',
    element: (
      <ParentProvider>
        <PublicParentRoute>
          <ParentDashboardPasswordReset />
        </PublicParentRoute>
      </ParentProvider>
    ),
  },
  {
    path: '/password-reset/:token',
    element: (
      <AuthProvider>
        <PublicRoute>
          <MainDashboardPasswordReset />
        </PublicRoute>
      </AuthProvider>
    ),
  },
  {
    path: '/parent/',
    element: (
      <ParentProvider>
        <ProtectedParentRoute>
          <ParentDashboardLayout />
        </ProtectedParentRoute>
      </ParentProvider>
    ),
    children: [
      {
        path: 'dashboard',
        element: <DashboardMain />,
      },
      {
        path: 'events',
        element: <Event />,
      },
      {
        path: 'attendance',
        element: <Attendance />,
      },
      {
        path: 'payment',
        element: <Payment />,
      },
      {
        path: 'student-profile',
        element: <StudentProfile />,
      },
      {
        path: 'teachers',
        element: <Teachers />,
      },
      {
        path: 'parent-guide',
        element: <ParentGuide />,
      },
      {
        path: 'settings',
        element: <Settings />,
        children: [
          { index: true, element: <AccountInformation /> },
          { path: 'security', element: <Security /> },
          { path: 'notifications', element: <Notifications /> },
        ],
      },
    ],
  },
  {
    path: '/parent/select-child',
    element: (
      <ParentProvider>
        <ProtectedParentRoute>
          <SelectChild />
        </ProtectedParentRoute>
      </ParentProvider>
    ),
  },
]);

function App() {
  return <RouterProvider router={router} />;
}

export default App;
